import type { CheckoutServiceOption, CheckoutServiceParticipant } from '@/scopes/checkout/domain/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { SwitchField } from '@sunwebgroup/uds-ui/fields/switch-field'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Menu } from '@sunwebgroup/uds-ui/menu'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import { ToggleButton, ToggleButtonGroup } from '@sunwebgroup/uds-ui/toggle-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import {
  saveEditingSkiAncillaryServiceValue,
  updateEditingSkiAncillaryServiceDuration,
  updateEditingSkiAncillaryServiceStartDate,
  updateEditingSkiAncillaryServiceValue,
} from '@/scopes/checkout/domain/actions/services/ski'
import { createSkiServiceEvent } from '@/scopes/checkout/domain/analytics'
import { useClickFilterSkiAncillaryService } from '@/scopes/checkout/domain/analytics/useClickFilterSkiAncillaryService'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import genericStyles from '../GenericService/GenericService.module.css'
import { getPriceForParticipant } from '../priceHelpers'
import { getDaysRangeFormatted, getPriceDifferenceAndPriceSymbol, shouldShowSignSelectedOption } from './helpers'
import {
  skiAncillaryDaysRangeByDurationSelector,
  skiAncillaryServiceDurationsSelector,
  skiAncillaryServiceEditingValueByIdSelector,
  skiAncillaryServiceHasIncludedOptionSelector,
  skiAncillaryServiceOptionsListSelector,
} from './selectors'
import styles from './SkiAncillaryComponents.module.css'
import {
  skiServiceIsBeingEdited,
  useSkiAncillaryServiceStore,
} from './store'

export function SkiPassService() {
  const intl = useIntl()
  const { service } = useService(ServiceType.SkiPass)
  useSendAncillaryAvailable(service, 'selectSkiPass')

  if (!service)
    return null

  const { rooms, participants, options } = service

  if (isEmpty(participants))
    return null
  if (isEmpty(options))
    return null

  const serviceDescription = `
    <ul>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_description_list_item_1' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_description_list_item_2' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_description_list_item_3' })}</li>
    </ul>
  `
  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_title' })}
      description={serviceDescription}
      glyph={getIconForService(ServiceType.SkiPass)}
    >
      <div className={styles.checkoutSectionContent}>
        {rooms?.map(({ participantIds }) => {
          return participantIds.map((participantId) => {
            const participant = participants?.[participantId]
            if (!participant || participant.availableOptions.length === 0) {
              return null
            }

            return (
              <ParticipantRow
                {...participant}
                key={participantId}
              />
            )
          })
        })}
        {isEmpty(rooms)
        && Object.values(participants ?? {}).map(participant => (
          <ParticipantRow
            {...participant}
            key={participant.id}
          />
        ))}
        <ParticipantMenuSelection />
      </div>
    </CheckoutSection>
  )
}

function ParticipantMenuSelection() {
  const { service } = useService(ServiceType.SkiPass)
  const [isParticipantMenuOpen, setIsParticipantMenuOpen] = React.useState(false)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useSkiAncillaryServiceStore()

  const handleClick = React.useCallback((participantId: string) => {
    if (!participantId)
      return
    if (!service)
      return
    const { participants } = service
    startEditingService({
      participantId,
      serviceType: ServiceType.SkiPass,
      isEditing: true,
    })
    if (isParticipantMenuOpen) {
      setIsParticipantMenuOpen(false)
    }
    sendEvent(
      createSkiServiceEvent({
        result: 'Add ski pass for',
        type: participants[participantId].name,
        description: 'Ski Pass',
        element: 'clickable text',
      }),
    )
  }, [sendEvent, isParticipantMenuOpen, startEditingService, service])

  if (!service)
    return null

  const { participants } = service

  const participantsWithoutServiceSelected = Object.values(participants).filter(({ selectedValue }) => !selectedValue)

  if (!participantsWithoutServiceSelected.length)
    return null

  function toggle() {
    setIsParticipantMenuOpen(!isParticipantMenuOpen)
  }

  return (
    <ListItem variant="outlined" className={styles.participantMenuSelectionListItem}>
      {participantsWithoutServiceSelected.length > 1 && (
        <Menu.Root open={isParticipantMenuOpen} onOpenChange={toggle}>
          <Menu.Trigger>
            <Button size="S" variant="secondary">{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_add_button_for' })}</Button>
          </Menu.Trigger>
          <Menu.Content>
            {participantsWithoutServiceSelected.map(participant => (
              <Menu.Item key={participant.id} onClick={() => handleClick(participant.id)}>
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_participant_menu_item' }, { name: participant.name })}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Root>
      )}
      {participantsWithoutServiceSelected.length === 1 && (
        <Button size="S" variant="secondary" onClick={() => handleClick(participantsWithoutServiceSelected[0].id)}>
          {values(participants).length > 1 && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_add_button_per_participant' }, { name: participantsWithoutServiceSelected[0].name })}
          {values(participants).length === 1 && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_add_button' })}
        </Button>
      )}
    </ListItem>
  )
}

function ParticipantRow(participant: CheckoutServiceParticipant) {
  const { selectedValue, name, id } = participant
  const { service } = useService(ServiceType.SkiPass)
  const { isDesktopSm } = useBreakpoints()
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useSkiAncillaryServiceStore()

  const handleClick = React.useCallback(() => {
    startEditingService({
      participantId: id,
      serviceType: ServiceType.SkiPass,
      isEditing: true,
    })

    sendEvent(
      createSkiServiceEvent({
        result: 'Change',
        type: name,
        description: 'Ski Pass',
        element: 'element clicked',
      }),
    )
  }, [sendEvent, startEditingService, name, id])

  if (!service)
    return null

  const { options, participants, isNoneOption } = service
  const { startDay, endDay } = selectedValue ? getDaysRangeFormatted(options[selectedValue].startDate, options[selectedValue].endDate, intl.locale) : { startDay: '', endDay: '' }
  const { isIncluded = false } = selectedValue ? options[selectedValue] : {}
  const hasIncludedOption = skiAncillaryServiceHasIncludedOptionSelector(service)

  const selectedOption = selectedValue ? options[selectedValue] : {} as CheckoutServiceOption
  const participantPrice = getPriceForParticipant(selectedOption, id)

  return (
    <>
      { selectedValue
      && (
        <ListItem variant="outlined" className={styles.participantRowListItem} onClick={handleClick}>
          <TitleListCell
            size="L"
            overline={Object.values(participants).length > 1
              ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_for_participant' }, { name })
              : ''}
            title={selectedValue ? options[selectedValue].label : intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_no_ski_pass_text' })}
            subtitle={selectedValue && selectedValue !== isNoneOption ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_selected_value_duration' }, { duration: options[selectedValue].duration, daysRange: intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_days_range' }, { startDay, endDay }) }) : ''}
          />
          <div className={styles.itemPrice}>
            <div className={styles.priceBlock}>
              <Typography as="span" className={styles.price}>
                { isIncluded
                  ? (!isDesktopSm ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_included_truncated' }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_included' }))
                  : intl.formatCurrency(participantPrice, { showSign: shouldShowSignSelectedOption(participantPrice, hasIncludedOption) })}
              </Typography>
              { !isDesktopSm && (
                <Button variant="transparentNeutral">
                  <Button.Icon glyph="ChevronRight" size={24} />
                </Button>
              )}
              { isDesktopSm && (
                <Button variant="secondary" size="S" onClick={handleClick}>
                  { isIncluded ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_upgrade_button' }) : intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_edit_button' })}
                </Button>
              )}
            </div>
          </div>
        </ListItem>
      )}
      <ServiceSheet participant={participant} />
    </>
  )
}

function ServiceSheet({ participant }: { participant: CheckoutServiceParticipant }) {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const { id: participantId, name, selectedValue } = participant
  const { service, setServiceValue, setServiceLocalValue } = useService(ServiceType.SkiPass)

  const participants = service?.participants
  const isNoneOption = service?.isNoneOption

  const [serviceAdded, setServiceAdded] = React.useState(selectedValue !== isNoneOption)
  const { endEditingService } = useSkiAncillaryServiceStore()

  const handleSaveValue = React.useCallback(() => {
    if (!service)
      return
    const { id } = service.editingSelectedValues[participantId]
    endEditingService(`${ServiceType.SkiPass}-${participantId}`)
    setServiceValue(saveEditingSkiAncillaryServiceValue({ participantId, value: id })(service))
    sendEvent(
      createSkiServiceEvent({
        result: `Save changes`,
        description: 'Ski pass',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, endEditingService, participantId, service, setServiceValue])

  const handleCancelClick = React.useCallback(() => {
    if (!participantId)
      return
    if (!service)
      return
    endEditingService(`${ServiceType.SkiPass}-${participantId}`)
    sendEvent(
      createSkiServiceEvent({
        result: `Cancel`,
        description: 'Ski pass',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, endEditingService, service, participantId])

  const toggleService = React.useCallback((value: boolean) => {
    setServiceAdded(value)

    if (!service)
      return
    if (!isNoneOption)
      return
    const { duration: durationSelected, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
    const availableOptionsForSelectedDuration = skiAncillaryServiceOptionsListSelector(service, durationSelected, startDateSelected)
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceValue({
        participantId,
        value: !value ? isNoneOption : availableOptionsForSelectedDuration[0],
      }),
    )

    sendEvent(
      createSkiServiceEvent({
        result: `Ski pass ${!value ? 'off' : 'on'}`,
        description: 'Ski pass',
        element: 'toggle',
        type: '',
      }),
    )
  }, [sendEvent, participantId, setServiceLocalValue, service, isNoneOption])
  const isParticipantBeingEdited = useSkiAncillaryServiceStore((skiServiceIsBeingEdited(`${ServiceType.SkiPass}-${participantId}`)))

  const handleOnOpenChange = (open: boolean) => {
    if (!open) {
      handleCancelClick()
    }
  }

  if (!service)
    return null

  return (

    <Sheet.Root open={isParticipantBeingEdited} onOpenChange={handleOnOpenChange}>
      <Sheet.Body side="right">
        <Sheet.Header
          titleLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_sheet_title' })}
          closeLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_cancel_text_button' })}
        />
        <Sheet.Content className={styles.skiAncillaryContent}>
          {participants && Object.values(participants).length > 1 && <Typography variant="display-h-regular">{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_for_participant_label' }, { name })}</Typography>}
          { isNoneOption
          && (
            <SwitchField
              variant="outlined"
              label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_remove_selection_label' })}
              defaultChecked={serviceAdded}
              onCheckedChange={toggleService}
            />
          )}
          { serviceAdded
          && (
            <>
              <DurationFilter participantId={participantId} />
              <RangeDateFilter participantId={participantId} />
              <SelectOptionFilter participantId={participantId} />
            </>
          )}
          { !serviceAdded && <RemoveBannerSelection participantId={participant.id} />}
        </Sheet.Content>
        <Sheet.Footer>
          <Button variant="primary" onClick={handleSaveValue} fullWidth>
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_accept_text_button' })}
          </Button>
        </Sheet.Footer>
      </Sheet.Body>
    </Sheet.Root>
  )
}

function RemoveBannerSelection({ participantId }: { participantId: string }) {
  const { service } = useService(ServiceType.SkiPass)

  const intl = useIntl()

  if (!service)
    return null
  const { options, isNoneOption } = service

  const selectedOption = isNoneOption ? options[isNoneOption] : {} as CheckoutServiceOption
  const participantPrice = getPriceForParticipant(selectedOption, participantId)

  return (
    <Banner
      color="informative"
      hasIcon
      text={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_remove_selection_title' }, { price: intl.formatCurrency(Math.abs(participantPrice)) })}
      size={'M' as any}
      supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_remove_selection_text' })}
    />
  )
}

function DurationFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiPass)
  const { sendSkiAncillaryDurationFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski pass')

  const handleDurationChange = React.useCallback((value: string | undefined) => {
    if (!value)
      return

    setServiceLocalValue(
      updateEditingSkiAncillaryServiceDuration({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryDurationFilterClickEvent(value)
  }, [sendSkiAncillaryDurationFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const durations = skiAncillaryServiceDurationsSelector(service)

  const { duration: durationSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId)

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_sheet_duration_text' })}>
      <ToggleButtonGroup className={styles.durationToggleGroup} type="single" defaultValue={durationSelected ?? ''} onValueChange={handleDurationChange}>
        {durations.map(({ value }) => (
          <ToggleButton key={value} title={value} value={value} disabled={value === durationSelected} />
        ))}
      </ToggleButtonGroup>
    </FormField>
  )
}

function RangeDateFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiPass)
  const { sendSkiAncillaryRangeFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski pass')

  const handleStartDateChange = React.useCallback((value: string) => {
    if (!value)
      return

    setServiceLocalValue(
      updateEditingSkiAncillaryServiceStartDate({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryRangeFilterClickEvent(participantId, value)
  }, [sendSkiAncillaryRangeFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { duration: durationSelected, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}

  const availableDaysRange = skiAncillaryDaysRangeByDurationSelector(service, durationSelected ?? '')

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_sheet_start_date_text' })}>
      <SelectableRadioButtonGroup value={startDateSelected} onValueChange={handleStartDateChange}>
        <div className={genericStyles.optionItemsContainer}>
          {availableDaysRange.map((range, index) => {
            const { start, end } = range
            const { startDay, endDay } = getDaysRangeFormatted(start, end, intl.locale)

            return (
              <SelectableListItem value={start} className={genericStyles.itemOptionWrapper} key={`${index}-${start}`}>
                <div className={genericStyles.itemOption}>
                  <div className={genericStyles.itemOptionControl}>
                    <RadioButton value={start} />
                  </div>
                  <div className={genericStyles.itemOptionContent}>
                    <Typography variant="body-m-regular-tight">
                      {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_days_range' }, { startDay, endDay })}
                    </Typography>
                  </div>
                </div>
              </SelectableListItem>
            )
          })}
        </div>
      </SelectableRadioButtonGroup>
    </FormField>
  )
}

function SelectOptionFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiPass)
  const { sendSkiAncillaryOptionFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski pass')

  const handleValueChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceValue({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryOptionFilterClickEvent(value)
  }, [sendSkiAncillaryOptionFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { options, participants } = service

  const { selectedValue, availableOptions: availableOptionsParticipant, ageCategory: participantAgeCategory } = participants[participantId]

  const { duration: durationSelected, id: optionSelectedId, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId)
  const availableOptionsForSelectedDuration = skiAncillaryServiceOptionsListSelector(service, durationSelected, startDateSelected)

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_pass_sheet_options_text' })}>
      <SelectableRadioButtonGroup value={optionSelectedId ?? undefined} onValueChange={handleValueChange}>
        <div className={genericStyles.optionItemsContainer}>
          {availableOptionsForSelectedDuration
            .filter(optionId => availableOptionsParticipant.includes(optionId))
            .map((optionId) => {
              const { id, label, badge, price, isIncluded } = options[optionId]

              const participantPrice = getPriceForParticipant(options[optionId], participantId)

              const { priceDifference } = getPriceDifferenceAndPriceSymbol(selectedValue, participantPrice, options, participantId)
              return (
                <SelectableListItem value={id} key={optionId} className={genericStyles.itemOptionWrapper}>
                  <div className={genericStyles.itemOption}>
                    <div className={genericStyles.itemOptionControl}>
                      <RadioButton value={id} />
                    </div>
                    <div className={genericStyles.itemOptionContent}>
                      {badge && <Tag variant="success-tinted" size="M" className={cn(genericStyles.itemOptionTag)}>{badge}</Tag>}
                      <Typography variant="body-m-regular-tight">
                        {label}
                        {' '}
                        {participantAgeCategory.toLowerCase() !== 'adult' && participantPrice !== price ? intl.formatMessage({ id: `checkout_steps_ancillaries_ski_ancillaries_${participantAgeCategory.toLowerCase()}_price_text` }) : ''}
                      </Typography>
                    </div>
                    <div className={genericStyles.itemOptionTrailing}>
                      <Typography as="span" className={styles.price}>
                        { isIncluded ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_included' }) : intl.formatCurrency(priceDifference, { showSign: priceDifference !== 0 })}
                      </Typography>
                    </div>
                  </div>
                </SelectableListItem>
              )
            },
            )}
        </div>
      </SelectableRadioButtonGroup>
    </FormField>
  )
}
